import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, type To } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api, formatApiError } from '../../lib/api'
import { track, trackCta } from '../../lib/analytics'
import { WAITLIST_MODE } from '../../lib/plans'
import { newFormSessionId, trackFormEvent } from '../../lib/formAnalytics'
import { ButtonLink, Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Modal } from '../ui/Modal'

/**
 * Waitlist gate for public launch (built 2026-09-19, see TODO.md's
 * "Waitlist gate for public launch" section). `source` records which
 * page/CTA a signup came from (e.g. "pricing", "home") for later funnel
 * analysis -- set by the caller, never typed by the visitor.
 *
 * Success copy deliberately does not promise email specifically -- there
 * is no outbound org-facing email wired up in this app yet (see
 * channel_settings.py) -- "we'll reach out" is accurate regardless of
 * which channel that ends up being.
 */
export function WaitlistForm({
  source,
  planInterest,
  onSubmitted,
}: {
  source: string
  planInterest?: string
  onSubmitted?: () => void
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Funnel analytics (2026-10-09): one session per time the form is shown.
  // Field NAMES and progress only -- never the typed values.
  const sessionId = useRef(newFormSessionId())
  const values: Record<string, string> = { name, email, note }
  const report = (event: 'view' | 'progress' | 'error', lastField?: string) =>
    trackFormEvent({
      sessionId: sessionId.current,
      form: 'waitlist',
      source,
      event,
      lastField,
      fieldsFilled: Object.keys(values).filter((k) => values[k].trim() !== ''),
    })
  useEffect(() => {
    report('view')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const fieldProps = (field: string) => ({
    onFocus: () => report('progress', field),
    onBlur: () => report('progress', field),
  })

  const submitMutation = useMutation({
    mutationFn: () =>
      api.post('/api/v1/waitlist', {
        name,
        email,
        plan_interest: planInterest ?? null,
        note: note || null,
        source,
        session_id: sessionId.current,
      }),
    onSuccess: () => {
      track('waitlist_joined', { source })
      setError(null)
      onSubmitted?.()
    },
    onError: (err) => {
      setError(formatApiError(err, 'Could not join the waitlist'))
      report('error')
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    submitMutation.mutate()
  }

  if (submitMutation.isSuccess) {
    return (
      <p role="status" className="text-body text-foreground/78">
        You're on the list — we'll reach out when spots open.
      </p>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <Input
        id="waitlist-name"
        label="Your name"
        autoComplete="name"
        placeholder="Ada Nwosu"
        value={name}
        onChange={(e) => setName(e.target.value)}
        {...fieldProps('name')}
        required
      />
      <Input
        id="waitlist-email"
        type="email"
        label="Work email"
        autoComplete="email"
        placeholder="you@yourcompany.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        {...fieldProps('email')}
        required
      />
      <Input
        id="waitlist-note"
        label="Anything we should know"
        placeholder="Optional"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        {...fieldProps('note')}
      />
      <div className="flex flex-wrap items-center gap-3.5">
        <Button
          type="submit"
          isLoading={submitMutation.isPending}
          className="min-h-11 w-full justify-center bg-magenta-700 text-white hover:bg-magenta-800"
        >
          Join the waitlist
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-sm text-danger-600">
          {error} Please try again, or write to{' '}
          <a className="underline" href="mailto:hello@getremindloop.com">
            hello@getremindloop.com
          </a>
          .
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        We use your details only to contact you about early access.{' '}
        <Link to="/privacy" className="underline">
          Privacy policy
        </Link>
      </p>
    </form>
  )
}

/**
 * Drop-in replacement for a "Get started" ButtonLink -- renders the
 * normal register link when WAITLIST_MODE is off, or a button that opens
 * the waitlist form in a modal when it's on. Everything else about the
 * CTA (label, className, styling) stays exactly as the caller passed it.
 */
export function WaitlistCta({
  to,
  source,
  planInterest,
  className,
  children,
}: {
  to: To
  source: string
  planInterest?: string
  className?: string
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)

  if (!WAITLIST_MODE) {
    return (
      <ButtonLink to={to} className={className} onClick={() => trackCta(source, planInterest)}>
        {children}
      </ButtonLink>
    )
  }

  return (
    <>
      <Button
        onClick={() => {
          trackCta(source, planInterest)
          setOpen(true)
        }}
        className={className}
      >
        {children}
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} title="Join the waitlist">
        <WaitlistForm source={source} planInterest={planInterest} />
      </Modal>
    </>
  )
}

/**
 * Text-style trigger for places that used to say "Sign up"/"Register"
 * (login footer, nav) -- opens the same waitlist modal.
 */
export function WaitlistLink({
  source,
  className,
  children,
}: {
  source: string
  className?: string
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>
        {children}
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Join the waitlist">
        <WaitlistForm source={source} />
      </Modal>
    </>
  )
}
